import { createSlice } from '@reduxjs/toolkit';
import {
  getEntriesST12Data,
  getEntryST12DetailData,
  getUrgentIncomingTrucks,
  getEntriesST12SummaryData,
  getEntriesST12NextHoursLastUpdateData,
  downloadEntriesST12
} from './async';
import EntryDetail from 'modules/inbound/entries/domain/NextHours/EntriesDetail';
import { EntriesST12DataState, SelectedSubHeaderFilters } from './types';
import { ENTRIES_NEXT_HOURS_COLUMNS } from 'modules/inbound/entries/domain/NextHours/EntriesNextHoursColumns';
import { EntriesSummary } from 'modules/inbound/entries/domain/NextHours/entriesSummaryMapper';
import { getColumnsFromLocalStorage } from 'modules/inbound/shared/utils/getColumnsFromLocalStorage';
import { getLocalStorage } from '../../../../../shared/utils/getLocalStorage';
import { downloadXlsxFile } from 'modules/shared/services/downloadXlsxFile';
import GetDateFormatedService from 'modules/inbound/shared/domain/GetDateFormatedService';

export const initialState: EntriesST12DataState = {
  entries: [],
  columns: ENTRIES_NEXT_HOURS_COLUMNS,
  urgentTrucks: {
    total: 0,
    entries: []
  },
  savedColumns: [],
  entriesRequestStatus: '',
  urgentTrucksRequestStatus: '',
  lastUpdate: '-',
  pagination: {
    page: 1,
    size: 50,
    pageCount: 0,
    totalCount: 0
  },
  selectedColumn: {
    key: '',
    isAscending: false
  },
  filters: {
    selectedSubHeaderFilters: {
      selectedWarehouses: [{ text: 'ST12', key: 'ST12' }],
      selectedSuppliers: [],
      selectedUnloadingPoints: []
    },
    search: '',
    urgentTrucks: false,
    criticalPieces: false
  },
  isFilterSaved: false,
  selectedEntry: null,
  entryDetailRequestStatus: '',
  entryDetail: EntryDetail.generateEmpty(),
  summary: {
    hasCriticalPieces: 0,
    urgents: 0
  },
  entriesFile: null,
  downloadRequestStatus: ''
};

export const ENTRIES_FILTERS_STATE_KEY = 'EntriesST12FiltersState';
const ENTRIES_NEXT_HOURS_COLUMNS_VIEW_STATE_KEY = 'EntriesST12ColumnsView';

const entriesST12Slice = createSlice({
  name: 'entriesST12',
  initialState,
  reducers: {
    updateCurrentPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    updateSelectedColumn: (state, action) => {
      state.selectedColumn = action.payload;
    },
    updateFilterSearch: (state, action) => {
      state.filters.search = action.payload;
    },
    updateSelectedWarehouses: (state, action) => {
      state.filters.selectedSubHeaderFilters.selectedWarehouses =
        action.payload;
      state.isFilterSaved = false;
    },
    updateSelectedUnloadingPoints: (state, action) => {
      state.filters.selectedSubHeaderFilters.selectedUnloadingPoints =
        action.payload;
      state.isFilterSaved = false;
    },
    updateSelectedSuppliers: (state, action) => {
      state.filters.selectedSubHeaderFilters.selectedSuppliers = action.payload;
      state.isFilterSaved = false;
    },
    updateFilterByUrgentrucks: (state) => {
      state.filters.urgentTrucks = !state.filters.urgentTrucks;
    },
    updateFilterByCriticalPieces: (state) => {
      state.filters.criticalPieces = !state.filters.criticalPieces;
    },
    resetFilters: (state, action) => {
      state.filters.selectedSubHeaderFilters =
        initialState.filters.selectedSubHeaderFilters;
      localStorage.removeItem(`${action.payload}-${ENTRIES_FILTERS_STATE_KEY}`);
      state.isFilterSaved = false;
    },
    saveFilters: (state, action) => {
      localStorage.setItem(
        `${action.payload}-${ENTRIES_FILTERS_STATE_KEY}`,
        JSON.stringify(state.filters.selectedSubHeaderFilters)
      );
      state.isFilterSaved = true;
    },
    loadFilters: (state, action) => {
      const localStoragefilters = getLocalStorage<SelectedSubHeaderFilters>(
        `${action.payload}-${ENTRIES_FILTERS_STATE_KEY}`,
        { ...state.filters.selectedSubHeaderFilters }
      );

      state.filters.selectedSubHeaderFilters = {
        selectedSuppliers: localStoragefilters.selectedSuppliers || [],
        selectedUnloadingPoints:
          localStoragefilters.selectedUnloadingPoints || [],
        selectedWarehouses: localStoragefilters.selectedWarehouses || []
      };
      state.isFilterSaved = Object.keys(localStoragefilters).some(
        (key) => localStoragefilters[key].length
      );
    },
    saveColumnsView: (state, action) => {
      localStorage.setItem(
        `${action.payload}-${ENTRIES_NEXT_HOURS_COLUMNS_VIEW_STATE_KEY}`,
        JSON.stringify(state.savedColumns)
      );
    },
    loadColumnsView: (state, action) => {
      state.savedColumns = getColumnsFromLocalStorage({
        username: action.payload,
        localStorageKey: ENTRIES_NEXT_HOURS_COLUMNS_VIEW_STATE_KEY,
        defaultColumns: [...state.columns]
      });
    },
    updateVisibleColumns: (state, action) => {
      state.savedColumns = state.savedColumns.map((item) =>
        item.key === action.payload.key
          ? { ...item, isVisibleCheck: !action.payload.isVisibleCheck }
          : item
      );
    },
    updateSelectedEntry: (state, action) => {
      state.selectedEntry = action.payload;
    },
    updateColumnsOrder: (state, action) => {
      state.savedColumns = action.payload;
    },
    resetDownloadStatus: (state) => {
      state.downloadRequestStatus = initialState.downloadRequestStatus;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getEntriesST12Data.fulfilled, (state, action) => {
      state.entries = action.payload.entries || [];
      state.entriesRequestStatus = action.meta.requestStatus;
      state.pagination = action.payload.pagination;
    });
    builder.addCase(getEntriesST12Data.pending, (state, action) => {
      state.entriesRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(getEntriesST12Data.rejected, (state, action) => {
      state.entriesRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(
      getEntriesST12NextHoursLastUpdateData.fulfilled,
      (state, action) => {
        state.lastUpdate = action.payload.lastUpdate;
      }
    );
    builder.addCase(getEntryST12DetailData.fulfilled, (state, action) => {
      state.entryDetail = action.payload;
      state.entryDetailRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(getEntryST12DetailData.pending, (state, action) => {
      state.entryDetailRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(getEntryST12DetailData.rejected, (state, action) => {
      state.entryDetailRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(getUrgentIncomingTrucks.fulfilled, (state, action) => {
      state.urgentTrucks = action.payload;
      state.urgentTrucksRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(getUrgentIncomingTrucks.pending, (state, action) => {
      state.urgentTrucksRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(getUrgentIncomingTrucks.rejected, (state, action) => {
      state.urgentTrucksRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(getEntriesST12SummaryData.fulfilled, (state, action) => {
      state.summary = action.payload;
    });
    builder.addCase(downloadEntriesST12.fulfilled, (state, action) => {
      state.entriesFile = action.payload;
      downloadXlsxFile({
        file: action.payload,
        fileName: `P_Next_24_hours_${GetDateFormatedService.formatCompleteISODate()}`
      });
      state.downloadRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(downloadEntriesST12.pending, (state, action) => {
      state.downloadRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(downloadEntriesST12.rejected, (state, action) => {
      state.downloadRequestStatus = action.meta.requestStatus;
    });
  }
});

//selectors
export const getEntries = (state) => state.entriesST12DataState.entries;
export const getColumns = (state) => state.entriesST12DataState.savedColumns;
export const getPageInfo = (state) => state.entriesST12DataState.pagination;
export const getIncomingEntriesLastUpdate = (state) =>
  state.entriesST12DataState.lastUpdate;
export const getUrgentTrucks = (state) =>
  state.entriesST12DataState.urgentTrucks;

export const getEntriesDataIsPending = (state) =>
  state.entriesST12DataState.entriesRequestStatus === 'pending';
export const getEntriesDataHasError = (state) =>
  state.entriesST12DataState.entriesRequestStatus === 'rejected';
export const getIsPendingUrgentTrucks = (state) =>
  state.entriesST12DataState.urgentTrucksRequestStatus === 'pending';
export const getHasErrorUrgentTrucks = (state) =>
  state.entriesST12DataState.urgentTrucksRequestStatus === 'rejected';
export const getSelectedColumn = (state) =>
  state.entriesST12DataState.selectedColumn;
export const getFilterSearch = (state) =>
  state.entriesST12DataState.filters.search;
export const getFilterByUrgentTrucks = (state) =>
  state.entriesST12DataState.filters.urgentTrucks;
export const getFilterByCriticalPieces = (state) =>
  state.entriesST12DataState.filters.criticalPieces;
export const getSelectedFilters = (state) =>
  state.entriesST12DataState.filters.selectedSubHeaderFilters;
export const getIsFilterSaved = (state) =>
  state.entriesST12DataState.isFilterSaved;
export const getHasFiltersSelected = (state) =>
  Object.keys(state.entriesST12DataState.filters.selectedSubHeaderFilters).some(
    (key) =>
      state.entriesST12DataState.filters.selectedSubHeaderFilters[key].length
  );
export const getSelectedEntry = (state) =>
  state.entriesST12DataState.selectedEntry;
export const getEntryDetailIsPending = (state) =>
  state.entriesST12DataState.entryDetailRequestStatus === 'pending';
export const getEntryDetailHasError = (state) =>
  state.entriesST12DataState.entryDetailRequestStatus === 'rejected';
export const getEntryDetails = (state) =>
  state.entriesST12DataState.entryDetail;
export const getEntriesSummary = (state): EntriesSummary =>
  state.entriesST12DataState.summary;
export const getDownloadEntriesIsPending = (state) =>
  state.entriesST12DataState.downloadRequestStatus === 'pending';
export const getDownloadEntriesSuccessful = (state) =>
  state.entriesST12DataState.downloadRequestStatus === 'fulfilled' &&
  state.entriesST12DataState.entriesFile !== null;
export const getDownloadEntriesError = (state) =>
  state.entriesST12DataState.downloadRequestStatus === 'rejected';

export const {
  updateCurrentPage,
  updateSelectedColumn,
  updateFilterSearch,
  updateFilterByUrgentrucks,
  updateFilterByCriticalPieces,
  updateSelectedWarehouses,
  updateSelectedSuppliers,
  updateSelectedUnloadingPoints,
  updateSelectedEntry,
  resetFilters,
  loadFilters,
  saveFilters,
  updateVisibleColumns,
  saveColumnsView,
  loadColumnsView,
  updateColumnsOrder,
  resetDownloadStatus
} = entriesST12Slice.actions;
export default entriesST12Slice.reducer;
